import { useState } from 'react';
import { avatarTemplates } from '../data/avatarTemplates';
import type { AvatarTemplate } from '../types/avatar';
import { TemplatePreviewCard } from '../components/avatar/TemplatePreviewCard';
import { AvatarEditorModal } from '../components/avatar/AvatarEditorModal';

/** Official WhatsApp Icon from better-icons (ri:whatsapp-fill) */
function WhatsAppIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M12.001 2c5.523 0 10 4.477 10 10s-4.477 10-10 10a9.95 9.95 0 0 1-5.03-1.355L2.005 22l1.352-4.968A9.95 9.95 0 0 1 2.001 12c0-5.523 4.477-10 10-10M8.593 7.3l-.2.008a1 1 0 0 0-.372.1a1.3 1.3 0 0 0-.294.228c-.12.113-.188.211-.261.306A2.73 2.73 0 0 0 6.9 9.62c.002.49.13.967.33 1.413c.409.902 1.082 1.857 1.97 2.742c.214.213.424.427.65.626a9.45 9.45 0 0 0 3.84 2.046l.568.087c.185.01.37-.004.556-.013a2 2 0 0 0 .833-.231a5 5 0 0 0 .383-.22q.001.002.125-.09c.135-.1.218-.171.33-.288q.126-.13.21-.302c.078-.163.156-.474.188-.733c.024-.198.017-.306.014-.373c-.004-.107-.093-.218-.19-.265l-.582-.261s-.87-.379-1.402-.621a.5.5 0 0 0-.176-.041a.48.48 0 0 0-.378.127c-.005-.002-.072.055-.795.931a.35.35 0 0 1-.368.13a1.4 1.4 0 0 1-.191-.066c-.124-.052-.167-.072-.252-.108a6 6 0 0 1-1.575-1.003c-.126-.11-.243-.23-.363-.346a6.3 6.3 0 0 1-1.02-1.268l-.059-.095a1 1 0 0 1-.102-.205c-.038-.147.061-.265.061-.265s.243-.266.356-.41c.11-.14.203-.276.263-.373c.118-.19.155-.385.093-.536q-.42-1.026-.868-2.041c-.059-.134-.234-.23-.393-.249q-.081-.01-.162-.016a3 3 0 0 0-.403.004z" />
    </svg>
  );
}

export function AvatarPage() {
  const [selectedTemplate, setSelectedTemplate] = useState<AvatarTemplate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const profileTemplates = avatarTemplates.filter((t) => t.category === 'avatar');
  const cardTemplates = avatarTemplates.filter((t) => t.category === 'card');

  const handleSelectTemplate = (template: AvatarTemplate) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="avatar-page">
      {/* 1. Hero Introduction */}
      <section className="avatar-hero">
        <div className="mx-auto max-w-4xl px-4">
          <p className="avatar-kicker">Campanha Digital · Traiano 55789</p>

          <h1 className="avatar-hero-title font-display mt-4">
            Apoie o Traiano
          </h1>

          <p className="avatar-hero-subtitle font-display">
            Juntos por um Paraná Melhor
          </p>

          <p className="avatar-hero-desc font-body">
            Crie o seu avatar e cards personalizados com a marca oficial do Traiano 55789 para
            demonstrar seu apoio nas redes sociais. Escolha um dos modelos abaixo, adicione uma foto
            sua direto da galeria ou da câmera e baixe a arte pronta para compartilhar!
          </p>
        </div>
      </section>

      {/* 2. Section: Profile Avatars (1:1) */}
      <section className="avatar-section-profile" aria-label="Modelos de foto de perfil">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <p className="avatar-kicker">Foto de Perfil</p>
          <h2 className="avatar-section-title font-display">
            Modelos para Redes Sociais
          </h2>
          <p className="avatar-section-subtitle font-body">
            Formatos circulares otimizados para foto de perfil no WhatsApp, Instagram, Facebook e X.
          </p>

          <div className="avatar-template-grid">
            {profileTemplates.map((tmpl) => (
              <TemplatePreviewCard
                key={tmpl.id}
                template={tmpl}
                onSelect={handleSelectTemplate}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 3. Section: Social Media Cards (4:5) */}
      <section className="avatar-section-cards" aria-label="Modelos de cards para redes sociais">
        <div className="mx-auto max-w-6xl px-4 text-center">
          <p className="avatar-kicker">Cards para Redes Sociais</p>
          <h2 className="avatar-section-title font-display">
            Cards para Feed e Stories
          </h2>
          <p className="avatar-section-subtitle font-body">
            Formatos verticais 4:5 prontos para publicação e engajamento no feed, stories e status.
          </p>

          <div className="avatar-template-grid">
            {cardTemplates.map((tmpl) => (
              <TemplatePreviewCard
                key={tmpl.id}
                template={tmpl}
                onSelect={handleSelectTemplate}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 4. Section: WhatsApp Stickers (Editorial Light Mode) */}
      <section id="figurinhas" className="avatar-section-stickers" aria-label="Figurinhas de WhatsApp">
        <div className="mx-auto max-w-6xl px-4">
          <div className="avatar-stickers-container">
            <p className="avatar-kicker">FIGURINHAS DE WHATSAPP</p>

            <h2 className="font-display">
              Figurinhas para o seu<br />
              <span>WhatsApp</span>
            </h2>

            <strong className="font-display">Traiano</strong>
            <small>PACOTE OFICIAL DE FIGURINHAS</small>

            <p className="font-body">
              Adicione o pacote de figurinhas oficial da campanha do Traiano e compartilhe o
              entusiasmo nas conversas com seus amigos, família e grupos.
            </p>

            <div>
              <a
                href="https://link.topstickers.app?p=H50qAIsXPM3"
                target="_blank"
                rel="noopener noreferrer"
                className="avatar-stickers-cta"
              >
                <WhatsAppIcon className="h-5 w-5 text-[#16a53a]" />
                <span>Baixar pacote de figurinhas</span>
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Interactive Canvas Editor Modal */}
      <AvatarEditorModal
        isOpen={isModalOpen}
        template={selectedTemplate}
        onClose={handleCloseModal}
      />
    </div>
  );
}
